import React from 'react';
import { Box, useColorModeValue } from '@chakra-ui/react';

/**
 * Tabla al 100% del ancho: celdas adaptables, headers visibles, acciones siempre a la vista.
 */
const tableFitSx = {
  '& table': {
    width: '100%',
    tableLayout: 'auto',
  },
  '& th': {
    whiteSpace: 'normal',
    overflow: 'visible',
    textOverflow: 'clip',
    overflowWrap: 'break-word',
    wordBreak: 'break-word',
    verticalAlign: 'bottom',
    lineHeight: '1.2',
    hyphens: 'auto',
    py: 1.5,
    px: 2,
  },
  '& td': {
    whiteSpace: 'normal',
    overflowWrap: 'break-word',
    wordBreak: 'break-word',
    verticalAlign: 'middle',
    fontWeight: 'normal',
    lineHeight: '1.25',
    py: 1,
    px: 2,
  },
  '& th > div': {
    maxWidth: '100%',
    minWidth: 0,
    flexWrap: 'wrap',
    alignItems: 'flex-start',
    whiteSpace: 'normal',
    overflow: 'visible',
  },
  '& td > div': {
    maxWidth: '100%',
    minWidth: 0,
    flexWrap: 'wrap',
  },
  '& th .chakra-text, & td .chakra-text': {
    whiteSpace: 'normal',
    overflowWrap: 'break-word',
    wordBreak: 'break-word',
    minWidth: 0,
    maxWidth: '100%',
    fontWeight: 'normal',
  },
  // Acciones: ancho fijo para que los iconos no queden aplastados/ocultos
  '& th:last-child, & td:last-child': {
    width: '6.5rem',
    minWidth: '6.5rem',
    maxWidth: '6.5rem',
    whiteSpace: 'nowrap',
    overflow: 'visible',
    overflowWrap: 'normal',
    wordBreak: 'normal',
    textAlign: 'right',
    paddingInline: '0.5rem',
  },
  '& td:last-child > div, & th:last-child > div': {
    flexWrap: 'nowrap',
    justifyContent: 'flex-end',
    maxWidth: 'none',
    minWidth: 'auto',
  },
};

/**
 * Contenedor de la tabla (paginado afuera).
 */
export const PageTableScroll = ({ children, ...rest }) => {
  const stripeBg = useColorModeValue('gray.50', 'whiteAlpha.100');
  const plainBg = useColorModeValue('white', 'navy.800');
  const cellTextColor = useColorModeValue('gray.700', 'gray.200');
  const rowHoverBg = useColorModeValue('gray.200', 'whiteAlpha.300');

  const zebraSx = {
    ...tableFitSx,
    '& table': {
      ...tableFitSx['& table'],
      color: cellTextColor,
    },
    '& th, & td': {
      color: cellTextColor,
    },
    '& th .chakra-text, & td .chakra-text': {
      ...tableFitSx['& th .chakra-text, & td .chakra-text'],
      color: cellTextColor,
    },
    '& tbody tr:nth-of-type(odd) td': {
      bg: stripeBg,
    },
    '& tbody tr:nth-of-type(even) td': {
      bg: plainBg,
    },
    '& tbody tr td': {
      transition: 'background 0.12s ease',
    },
    '& tbody tr:hover td': {
      bg: `${rowHoverBg} !important`,
    },
  };

  return (
    <Box w="100%" maxW="100%" minW={0} overflowX="hidden" sx={zebraSx} {...rest}>
      {children}
    </Box>
  );
};

/**
 * Contenedor de tablas admin.
 * Usar PageTable.Scroll alrededor de <Table>.
 */
const PageTable = ({ children, ...rest }) => {
  const borderColor = useColorModeValue('gray.200', 'whiteAlpha.200');
  const bg = useColorModeValue('white', 'navy.800');

  const {
    overflowX: _ignoredOverflowX,
    overflow: _ignoredOverflow,
    stickyActions: _ignoredSticky,
    ...restProps
  } = rest;

  return (
    <Box
      w="100%"
      maxW="100%"
      minW={0}
      display="flex"
      flexDirection="column"
      bg={bg}
      borderWidth="1px"
      borderColor={borderColor}
      borderRadius="md"
      overflow="hidden"
      {...restProps}
    >
      {children}
    </Box>
  );
};

PageTable.Scroll = PageTableScroll;

export default PageTable;
